import React from 'react';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  Heading,
  Icon,
  Pressable,
  HStack,
  VStack,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../../../common/context/DialogContext';
import { formatRelativeTime } from '../../../../common/utils/formatRelativeTime';
import { VersionHistoryPanel } from './VersionHistoryPanel';

export interface TaskSubmissionVersionSummary {
  versionSeq: number;
  createdAt: string;
  sizeChars: number;
  pinned: boolean;
  contentHash: string;
}

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  versions: TaskSubmissionVersionSummary[];
  currentCharCount?: number;
  isLoading: boolean;
  isError: boolean;
  onLoadVersion: (versionSeq: number) => void;
}

export const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  versions,
  currentCharCount = 0,
  isLoading,
  isError,
  onLoadVersion,
}) => {
  const { t } = useTranslation();
  const { showDialog } = useDialog();

  const handleLoadVersion = (versionSeq: number) => {
    const version = versions?.find((v) => v.versionSeq === versionSeq);
    if (!version) return;

    const relativeDate = formatRelativeTime(version.createdAt);

    showDialog({
      title: t('student.task.versionHistory.loadConfirm.title', 'Load Previous Version?'),
      message: t('student.task.versionHistory.loadConfirm.message', {
        date: relativeDate,
        defaultValue: `Replace your current work with version from ${relativeDate}? Your current draft will be saved as a new version in history.`,
      }),
      buttons: [
        {
          text: t('student.task.versionHistory.loadConfirm.cancel', 'Cancel'),
          variant: 'outline',
          action: 'secondary',
        },
        {
          text: t('student.task.versionHistory.loadConfirm.confirm', 'Load Version'),
          variant: 'solid',
          action: 'primary',
          onPress: () => {
            onLoadVersion(versionSeq);
            onClose();
          },
        },
      ],
    });
  };

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose} size="lg">
      <AlertDialogBackdrop />
      <AlertDialogContent maxHeight="80%" minHeight={400} testID="version-history-modal">
        <AlertDialogHeader borderBottomWidth={1} borderBottomColor="$borderLight200" pb="$3">
          <HStack justifyContent="space-between" alignItems="center" width="100%">
            <Heading size="lg">
              {t('student.task.versionHistory.title', 'Version History')}
            </Heading>
            <Pressable onPress={onClose} testID="close-version-history" p="$1">
              <Icon as={X} size="lg" color="$textSubtle" />
            </Pressable>
          </HStack>
        </AlertDialogHeader>
        <AlertDialogBody p="$0">
          <VStack height={400}>
            <VersionHistoryPanel
              versions={versions}
              currentCharCount={currentCharCount}
              isLoading={isLoading}
              isError={isError}
              onLoadVersion={handleLoadVersion}
            />
          </VStack>
        </AlertDialogBody>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default VersionHistoryModal;
